import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import dataService from 'services/dataService';

import { GET_CARE_MANAGERS_KEY } from '../agency-doctor';
import { showResult } from '../utils';
import { type TPotentialPatientsCountFilter } from './use-get-potential-transfer-count';

type TransferPatientFilets = Pick<
  TPotentialPatientsCountFilter,
  | 'clinicGuidsIncluded'
  | 'clinicGuidsExcluded'
  | 'language'
  | 'ccm'
  | 'pcm'
  | 'newlyEnrolledOnly'
  | 'followUpOnly'
  | 'partialsOnly'
>;

type TTransferPatients = Pick<
  TPotentialPatientsCountFilter,
  'toAgencyDoctorGuid' | 'fromAgencyDoctorGuid' | 'patientsCountToTransfer'
> & { filters: TransferPatientFilets };

export type TTransferPatientsRes = {
  transferredPatientsCount: number;
};

const setTransferPatientsFn = async (postData: TTransferPatients) => {
  const url = 'core-2/transfer-tool/transfer-patients';
  const { error, data } = await dataService.createOne<TTransferPatientsRes>(
    url,
    postData,
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

type Props = {
  onSuccess: () => void;
};

export const useSetTransferPatients = (props: Props) => {
  const { onSuccess } = props;
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation({
    mutationFn: async (body: TTransferPatients) => setTransferPatientsFn(body),
    onSuccess() {
      notificationController.success({
        message: t('messages.patientTransferSuccessMessage'),
      });
      queryClient.invalidateQueries({ queryKey: [GET_CARE_MANAGERS_KEY] });
      onSuccess();
    },
  });
  return {
    setTransferPatients: mutate,
    updateLoading: isLoading,
  };
};
